<template>
  <section class="section-base form-section-base form-base">
    <div class="form-section-base__title">Сведения о заявителе</div>
    <v-container>
      <div class="form-section-base__controls">
        <v-row>
          <v-col>
            <v-radio-group v-model="role" row>
              <v-radio
                label="Физическое лицо"
                :value="Role.PERSON"
                :disabled="roleType !== Role.PERSON"
              />
              <v-radio
                label="Юридическое лицо"
                :value="Role.ORGANIZATION"
                :disabled="roleType !== Role.ORGANIZATION"
              />
              <v-radio
                label="Иностранное юридическое лицо"
                :value="Role.FOREIGN_ORGANIZATION"
                :disabled="
                  ![Role.FOREIGN_ORGANIZATION, Role.ORGANIZATION].includes(
                    roleType
                  )
                "
              />
            </v-radio-group>
          </v-col>
        </v-row>
      </div>

      <Person v-if="role === Role.PERSON" :v="$v.person" />

      <Organization v-if="role === Role.ORGANIZATION" :v="$v.organization" />

      <ForeignOrganization
        v-if="role === Role.FOREIGN_ORGANIZATION"
        :v="$v.foreignOrganization"
      />

      <Footer
        @next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
        @prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
        :first-step="isFirstStep"
        :loading-next="loadingNext"
      />
    </v-container>
  </section>
</template>

<script>
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';

import { Role } from '@/utils/dictionaries';
import { checkInvalid, leaveStep } from '@/utils/services';
import Footer from '@/components/services/elements/Footer';
import { StepDirection, StepName } from '@/types/services';
import Person, { personModel, personValidation } from './Person';
import Organization, {
  organizationModel,
  organizationValidation,
} from './Organization';
import ForeignOrganization, {
  foreignOrganizationModel,
  foreignOrganizationValidation,
} from './ForeignOrganization';
import { ServicesModule } from '@/store/modules/services';
import { AuthModule } from '@/store/modules/auth';
import { RodStep } from '@/constants';

export default {
  name: 'Manual',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
    roleType: {
      type: String,
      required: true,
    },
  },
  components: {
    Person,
    Organization,
    ForeignOrganization,
    Footer,
  },
  data() {
    return {
      StepDirection,
      StepName,
      leaveStep,
      Role,
      invalidFields: [],
      role: this.roleType,
      trusteeRole: Role.PERSON,
      person: cloneDeep(personModel),
      organization: cloneDeep(organizationModel),
      foreignOrganization: cloneDeep(foreignOrganizationModel),
    };
  },
  computed: {
    user() {
      return AuthModule.user;
    },
    isFirstStep() {
      return RodStep.APPLICANT === StepName.S1;
    },
  },
  validations() {
    return {
      person: personValidation,
      organization: organizationValidation,
      foreignOrganization: foreignOrganizationValidation,
    };
  },
  created() {
    this.prefillDraft();
  },
  methods: {
    prefillDraft() {
      const applicantStore = cloneDeep(
        ServicesModule.statement.subjects.applicant
      );

      if (
        !applicantStore.person &&
        !applicantStore.organization &&
        !applicantStore.foreignOrganization
      ) {
        this.prefillEsia();
        return;
      }

      if (applicantStore.organization || applicantStore.foreignOrganization) {
        this.prefillEsia();
      }

      if (applicantStore.person) {
        this.role = Role.PERSON;
        this.person = deepmerge.all([
          personModel,
          applicantStore.person,
          {
            identityDocument: {
              ...personModel.identityDocument,
              ...applicantStore.identityDocument,
            },
          },
        ]);
      }

      if (applicantStore.organization) {
        this.role = Role.ORGANIZATION;
        this.organization = deepmerge(
          organizationModel,
          applicantStore.organization
        );
      }

      if (applicantStore.foreignOrganization) {
        this.role = Role.FOREIGN_ORGANIZATION;
        this.foreignOrganization = deepmerge(
          foreignOrganizationModel,
          applicantStore.foreignOrganization
        );
      }
    },

    prefillEsia() {
      if (this.roleType === Role.PERSON) {
        this.prefillEsiaPerson();
      } else if (this.roleType === Role.ORGANIZATION) {
        this.prefillEsiaOrganization();
        this.prefillEsiaForeignOrganization();
      }     
    },

    prefillEsiaPerson() {
      this.person = deepmerge(this.person, {
        lastName: this.user.lastName || null,
        firstName: this.user.firstName || null,
        patronymic: this.user.middleName || null,
        snils: this.user.snils || null,
        inn: this.user.inn || null,
        identityDocument: {
          number: this.user.identityDoc.number || '',
          series: this.user.identityDoc.series || '',
        },
      });
    },

    async prefillEsiaOrganization() {
      const userDetails = cloneDeep(AuthModule.user);
      const orgDetails = cloneDeep(AuthModule.orgDetails);

      this.organization = deepmerge(this.organization, {
        name: orgDetails.fullName || null,
        ogrn: orgDetails.ogrn || null,
        inn: orgDetails.inn || null,
        assigneeLastName: userDetails.lastName || null,
        assigneeName: userDetails.firstName || null,
        assigneePatronymic: userDetails.middleName || null,
        assigneeSnils: userDetails.snils || null,
      });
    },

    async prefillEsiaForeignOrganization() {
      const userDetails = cloneDeep(AuthModule.user);
      const orgDetails = cloneDeep(AuthModule.orgDetails);

      this.foreignOrganization = deepmerge(this.foreignOrganization, {
        name: orgDetails.fullName || null,
        inn: orgDetails.inn || null,
        assigneeLastName: userDetails.lastName || null,
        assigneeName: userDetails.firstName || null,
        assigneePatronymic: userDetails.middleName || null,
        assigneeSnils: userDetails.snils || null,
      });
    },

    assemblyModel() {
      const applicant = {};

      applicant[this.role] = cloneDeep(this[this.role]);

      if (this.role === Role.PERSON) {
        applicant.identityDocument = applicant.person.identityDocument;
        delete applicant.person.identityDocument;
      }

      return applicant;
    },

    touchFields() {
      this.invalidFields = [];

      this.$v[this.role].$touch();
      checkInvalid(this.$v[this.role], this.invalidFields);
    },

    btnSaveClickHandler(direction) {
      leaveStep({
        stepThis: this,
        direction,
        step: RodStep.APPLICANT,
      });
    },
  },
};
</script>
